import {
    type AiAgentId,
    ERRORS_METRIC_ID,
    getThreadErrorTypeDescription,
    getThreadErrorTypeLabel,
    getThreadMetricDisplayName,
    getThreadMetricFieldIdsForAgent,
    getThreadMetricsLastCapturedAt,
} from '@fruitful/llm-service-shared';
import {
    THREAD_ERROR_TYPES,
    type ThreadError,
    type ThreadErrorType,
} from '@fruitful/types';
import {
    BodyMediumEmphasized,
    BodySmallEmphasized,
    BodySmallRegular,
    InfoButtonWithTooltip,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import { useGetMastraThread } from '../hooks/useGetMastraThread';
import { useLiveThreadRefetchInterval } from '../hooks/useLiveThreadRefetchInterval';
import {
    formatThreadMetricFieldValue,
    getThreadErrorTypes,
    getThreadMetrics,
    getThreadMetricValue,
} from '../utils/threadMetadataUtils';

const MetricRow = ({
    label,
    value,
}: {
    label: string;
    value: string | null;
}) => {
    const hasValue = value != null && value !== '';

    return (
        <XStack
            justifyContent="space-between"
            alignItems="flex-start"
            gap="$3"
            paddingVertical={8}
            borderBottomWidth={1}
            borderBottomColor="$gray200"
        >
            <BodySmallRegular color="$gray1000" flexShrink={0}>
                {label}
            </BodySmallRegular>
            {hasValue ? (
                <BodySmallEmphasized
                    color="$gray1200"
                    textAlign="right"
                    flex={1}
                >
                    {value}
                </BodySmallEmphasized>
            ) : (
                <BodySmallRegular color="$gray500">—</BodySmallRegular>
            )}
        </XStack>
    );
};

export const AiThreadMetricsPanel = ({
    threadId,
    agentId,
    isRunningEval = false,
}: {
    threadId: string;
    agentId: AiAgentId;
    isRunningEval?: boolean;
}) => {
    const refetchInterval = useLiveThreadRefetchInterval({
        additionalShouldPoll: isRunningEval,
    });

    const {
        data: thread,
        isLoading,
        error,
    } = useGetMastraThread({
        threadId,
        agentId,
        refetchInterval,
    });

    const metrics = getThreadMetrics(thread?.metadata);
    const capturedAt = getThreadMetricsLastCapturedAt(thread?.metadata);
    const metricFieldIds = getThreadMetricFieldIdsForAgent(agentId);

    const isBusy = isLoading || isRunningEval;

    return (
        <YStack width="100%" gap="$2">
            <BodyMediumEmphasized>Metrics</BodyMediumEmphasized>
            <YStack minHeight={160} position="relative">
                {error != null ? (
                    <BodySmallRegular color="$red900">
                        Unable to load thread metrics.
                    </BodySmallRegular>
                ) : metrics == null ? (
                    isBusy ? null : (
                        <BodySmallRegular color="$gray700">
                            No metrics captured for this thread yet.
                        </BodySmallRegular>
                    )
                ) : (
                    <YStack opacity={isBusy ? 0.35 : 1}>
                        {metricFieldIds
                            .filter(
                                (metricFieldId) =>
                                    metricFieldId !== ERRORS_METRIC_ID,
                            )
                            .map((metricFieldId) => {
                                const value = getThreadMetricValue(
                                    { metrics },
                                    metricFieldId,
                                );
                                const formatted =
                                    value == null || value === ''
                                        ? null
                                        : formatThreadMetricFieldValue(
                                              metricFieldId,
                                              value,
                                          );

                                return (
                                    <MetricRow
                                        key={metricFieldId}
                                        label={getThreadMetricDisplayName(
                                            metricFieldId,
                                        )}
                                        value={formatted}
                                    />
                                );
                            })}
                        <MetricRow
                            label="Captured at"
                            value={
                                capturedAt == null
                                    ? null
                                    : formatToSimpleDateTimeString(capturedAt)
                            }
                        />
                    </YStack>
                )}
                <YStack opacity={isBusy ? 0.35 : 1}>
                    <ErrorsSection
                        errors={getThreadErrorTypes({ metrics })}
                        hasCapturedMetrics={capturedAt != null}
                    />
                </YStack>
                {isBusy ? (
                    <YStack
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="rgba(255,255,255,0.72)"
                    >
                        <Spinner />
                    </YStack>
                ) : null}
            </YStack>
        </YStack>
    );
};

const getErrorTypeTooltip = (
    errorType: ThreadErrorType,
    errors: ThreadError[],
): string => {
    const description = getThreadErrorTypeDescription(errorType);
    if (errorType !== 'tool') {
        return description;
    }

    const toolNames = errors.flatMap((error) =>
        error.type === 'tool' ? [error.name] : [],
    );

    if (toolNames.length === 0) {
        return description;
    }

    return `${description} Failed tools: ${toolNames.join(', ')}.`;
};

const ErrorIndicatorRow = ({
    label,
    tooltip,
    detected,
    hasCapturedMetrics,
    isSectionHeader = false,
}: {
    label: string;
    tooltip: string;
    detected: boolean;
    hasCapturedMetrics: boolean;
    isSectionHeader?: boolean;
}) => {
    return (
        <XStack
            justifyContent="space-between"
            alignItems="center"
            gap="$3"
            paddingVertical={8}
            borderBottomWidth={1}
            borderBottomColor="$gray200"
        >
            <XStack alignItems="center" gap={4} flexShrink={0}>
                {isSectionHeader ? (
                    <BodyMediumEmphasized>{label}</BodyMediumEmphasized>
                ) : (
                    <BodySmallRegular color="$gray1000">
                        {label}
                    </BodySmallRegular>
                )}
                <InfoButtonWithTooltip tooltipContent={tooltip} subtle />
            </XStack>
            {hasCapturedMetrics ? (
                <BodySmallEmphasized color={detected ? '$red900' : '$gray1200'}>
                    {detected ? 'Yes' : 'No'}
                </BodySmallEmphasized>
            ) : (
                <BodySmallRegular color="$gray500">—</BodySmallRegular>
            )}
        </XStack>
    );
};

const ErrorsSection = ({
    errors,
    hasCapturedMetrics,
}: {
    errors: ThreadError[];
    hasCapturedMetrics: boolean;
}) => {
    return (
        <YStack paddingTop="$3">
            <ErrorIndicatorRow
                label="Errors"
                tooltip="Yes if any classified error fired on this thread."
                detected={errors.length > 0}
                hasCapturedMetrics={hasCapturedMetrics}
                isSectionHeader
            />
            {THREAD_ERROR_TYPES.map((errorType) => (
                <ErrorIndicatorRow
                    key={errorType}
                    label={getThreadErrorTypeLabel(errorType)}
                    tooltip={getErrorTypeTooltip(errorType, errors)}
                    detected={errors.some((error) => error.type === errorType)}
                    hasCapturedMetrics={hasCapturedMetrics}
                />
            ))}
        </YStack>
    );
};
